// ═══════════════════════════════════════════════════════════════════════════════════════════
// PublishPreviewDrawer — the BODY of the preview level (chrome: `ClalDrawerStack`).
//
// THE DIFF IS REAL, AND IT IS PER-ITEM (§C6):
//   · the "before" is the item AS IT WAS WHEN THE EDIT DRAWER OPENED — a client-side snapshot,
//     exactly like the mock. There is NO server GET for "what is currently published"; the
//     published state lives in a JSON file on disk, and inventing an endpoint for it is not in
//     the contract.
//   · the diff is computed against that snapshot and nothing else.
// The mock originally shipped a HARD-CODED diff line, which read as fake to every reviewer
// (05-REVIEW-PANEL P1 #11). If nothing changed, this says so.
//
// The publish button here is TWO CALLS in order: `SaveItem`, then `Publish{ItemIds:[id]}`. If the
// first succeeds and the second fails the copy MUST be the partial one — "נשמר, אך הפרסום נכשל"
// — never "האתר לא השתנה", because the save did happen. The screen owns that branch; this file
// only asks for it.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { CC, eClalItemType } from '../../../Models/ClalCenter/ClalCenter';
import { norm } from '../searchNormalizer';
import { ItemDraft } from './ItemDrawer';
import PortalRowPreview from './PortalRowPreview';

/** Returns the ready-to-render diff sentence. */
export function describeDiff(
    draft: ItemDraft,
    snapshot: ItemDraft | null,
    t: (key: string, opts?: any) => string
): string {
    if (!draft.ItemID || !snapshot) return t(`${CC}preview.diffNew`);

    const parts: string[] = [];
    if (draft.Title.trim() !== snapshot.Title.trim()) parts.push(t(`${CC}preview.diffTitle`));

    // Keyword comparison uses the FROZEN normaliser, so re-typing "קופ״ג" as "קופג" is correctly
    // reported as no change — the search engine cannot tell them apart either.
    const before = snapshot.Keywords.map(norm);
    const now = draft.Keywords.map(norm);
    const added = now.filter(k => before.indexOf(k) === -1).length;
    const removed = before.filter(k => now.indexOf(k) === -1).length;
    if (added) {
        parts.push(added === 1
            ? t(`${CC}preview.diffKeywordsAddedOne`)
            : t(`${CC}preview.diffKeywordsAddedMany`, { n: added }));
    }
    if (removed) {
        parts.push(removed === 1
            ? t(`${CC}preview.diffKeywordsRemovedOne`)
            : t(`${CC}preview.diffKeywordsRemovedMany`, { n: removed }));
    }

    // Description is rendered under every card on the portal, so a description-only edit is a
    // real, agent-visible change. It was missing from this comparison entirely, which made the
    // publish gate answer "no changes were made" about text that was about to go live.
    if ((draft.Description || '').trim() !== (snapshot.Description || '').trim()) {
        parts.push(t(`${CC}preview.diffDescription`));
    }

    if (draft.ItemType !== snapshot.ItemType) parts.push(t(`${CC}preview.diffType`));
    else if (draft.ItemType === eClalItemType.LINK && draft.Url.trim() !== snapshot.Url.trim()) {
        parts.push(t(`${CC}preview.diffUrl`));
    } else if (draft.ItemType === eClalItemType.FILE && draft.FileRef) {
        parts.push(t(`${CC}preview.diffFile`));
    } else if (draft.ItemType === eClalItemType.INFO && draft.InfoText.trim() !== snapshot.InfoText.trim()) {
        parts.push(t(`${CC}preview.diffInfo`));
    }

    if (draft.GroupID !== snapshot.GroupID) parts.push(t(`${CC}preview.diffLocation`));

    return parts.length ? t(`${CC}preview.diffPrefix`) + parts.join(' · ') : t(`${CC}preview.diffNone`);
}

interface Props {
    draft: ItemDraft;
    snapshot: ItemDraft | null;
    /** Path label of the destination, so the preview says where it will land. */
    pathLabel: string;
    busy: boolean;
    isRTL: boolean;
    /**
     * Opened from the 👁 in a row rather than from the edit drawer: LOOK ONLY. No diff (there is
     * nothing to diff against) and NO publish button.
     *
     * ⚠️ This flag is why the mock's most dangerous bug cannot come back: there, 👁 opened the
     * live publish drawer against an EMPTY form, so confirming it overwrote the item, wiped its
     * keywords and moved it into a sub-group with a blank name (05-REVIEW-PANEL P1 #1).
     */
    previewOnly?: boolean;
    onPublish: () => void;
    onBack: () => void;
}

const PublishPreviewDrawer = ({
    draft, snapshot, pathLabel, busy, isRTL, previewOnly = false, onPublish, onBack
}: Props) => {
    const { t } = useTranslation();

    const fileName = draft.FileRef?.OriginalName ?? draft.ExistingFileName ?? '';
    // A file uploaded in this session wins over the one already on the item — same precedence
    // the name above uses, so the three never disagree with each other.
    const mime = draft.FileRef?.Mime ?? draft.ExistingMime ?? null;
    const sizeBytes = draft.FileRef?.SizeBytes ?? draft.ExistingSizeBytes ?? null;

    return (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Typography style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5A6775)' }}>{pathLabel}</Typography>

            {/*
              THE PORTAL ROW ITSELF — not an approximation of it. See PortalRowPreview.tsx for the
              line-by-line provenance. What stood here was a flex row that got six things wrong,
              of which the instructive one was the chevron: it was the TEXT character U+2039,
              chosen by `isRTL ? '‹' : '›'`, and U+2039/U+203A are Bidi-MIRRORING characters. The
              ternary picked the correct glyph and the Bidi algorithm then reversed it, so the
              preview's chevron pointed the opposite way from the site's. The portal draws an SVG
              path, which never mirrors — so the mirror draws one too.

              isRTL is deliberately NOT passed down: the portal is <html dir="rtl"> for every
              visitor, so the row is rtl regardless of which language the editor's admin is in.
            */}
            <PortalRowPreview
                itemType={draft.ItemType}
                title={draft.Title}
                description={draft.Description}
                fileName={fileName}
                mime={mime}
                sizeBytes={sizeBytes}
                infoText={draft.InfoText}
                /* The publish button is disabled without a title and an existing item cannot
                   have one, so this is all but unreachable — which is precisely why it is a dash
                   and not a translation key: an un-translated key paints 'cc-preview.untitled'
                   on screen, which is worse than the dash it replaced. */
                emptyTitle="—"
            />

            {/* Below this point is EDITOR information, not site content: the portal shows neither
                the target url nor the file's name (the name rides on the download attribute, the
                url on the href). They stay because this is the last gate before publishing and
                "which file exactly" is worth one line — but they sit outside the white card, which
                is now visibly the site and nothing else. */}
            {draft.ItemType === eClalItemType.LINK && !!draft.Url && (
                <Typography style={{ fontSize: 13, direction: 'ltr', textAlign: isRTL ? 'right' : 'left', color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                    {draft.Url}
                </Typography>
            )}
            {draft.ItemType === eClalItemType.FILE && !!fileName && (
                <Typography style={{ fontSize: 13, direction: 'ltr', textAlign: isRTL ? 'right' : 'left', color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                    {fileName}
                </Typography>
            )}

            {!!draft.Keywords.length && (
                <Box style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {draft.Keywords.map(k => (
                        <Typography
                            key={k}
                            component="span"
                            style={{
                                fontSize: 12, background: 'var(--pls-d-surface-2, #f0f4f8)', border: '1px solid var(--pls-d-border, #dbe3ea)',
                                borderRadius: 999, padding: '0 9px', color: 'var(--pls-d-text-2, #44525e)'
                            }}
                        >
                            {k}
                        </Typography>
                    ))}
                </Box>
            )}

            {!previewOnly && (
                <Typography style={{ fontSize: 13, color: 'var(--pls-d-text-2, #5A6775)' }}>
                    {describeDiff(draft, snapshot, t)}
                </Typography>
            )}

            <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
                {!previewOnly && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onPublish}
                        disabled={busy || !draft.Title.trim()}
                        style={{ fontWeight: 700 }}
                    >
                        {t(`${CC}preview.publish`)}
                    </Button>
                )}
                <Button onClick={onBack} disabled={busy} style={{ color: 'var(--pls-d-text-2, #5b6b7b)', fontWeight: 600 }}>
                    {previewOnly ? t(`${CC}close`) : t(`${CC}preview.backToEdit`)}
                </Button>
            </Box>
        </Box>
    );
};

export default PublishPreviewDrawer;
